import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './StudentAssignments.css';
import {
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  Button, 
  CircularProgress, 
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import UploadIcon from '@mui/icons-material/Upload';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import GradeIcon from '@mui/icons-material/Grade';
import QuizIcon from '@mui/icons-material/Quiz';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';

interface QuizInfo {
  quiz_assignment_id: number;
  quiz_title: string | null;
  due_date: string | null;
  allow_retakes: boolean;
  max_attempts: number;
  attempts_used: number;
  best_score: number | null;
  best_possible: number | null;
  best_percentage: number | null;
  latest_status: string | null;
}

interface Assignment {
  id: number;
  document_id: number;
  document_name: string;
  subject: string;
  reading_level: number;
  grade_level: string;
  assigned_date: string;
  due_date: string | null;
  teacher_name: string;
  has_tailored_version: boolean;
  tailored_document_id: number | null;
  has_interactive_version: boolean;
  interactive_document_id: number | null;
  completion_status: string;
  is_overdue: boolean;
  assignment_type: 'submittable' | 'informational';
  quizzes: QuizInfo[];
}

interface Submission {
  id: number;
  assignment_id: number;
  student_id: number;
  status: string;
  submission_text: string | null;
  submission_notes: string | null;
  original_filename: string | null;
  has_file: boolean;
  grade: string | null;
  points_earned: number | null;
  points_possible: number | null;
  teacher_feedback: string | null;
  submitted_at: string | null;
  graded_at: string | null;
}

const StudentAssignments: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({ open: false, message: '', severity: 'info' });
  
  // Submission dialog state
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [currentSubmission, setCurrentSubmission] = useState<Submission | null>(null);
  const [submissionText, setSubmissionText] = useState('');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchAssignments();
  }, []);

  useEffect(() => {
    filterAssignments();
  }, [assignments, filter, showCompleted]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_BASE_URL}/api/student/assignments`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setAssignments(response.data.assignments);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const filterAssignments = () => {
    let filtered = [...assignments];

    // Hide completed unless toggled on
    if (!showCompleted) {
      filtered = filtered.filter(a => a.completion_status !== 'completed');
    }

    switch (filter) {
      case 'pending':
        filtered = filtered.filter(a => a.completion_status === 'assigned');
        break;
      case 'inProgress':
        filtered = filtered.filter(a => a.completion_status === 'in_progress');
        break;
      case 'completed':
        filtered = filtered.filter(a => a.completion_status === 'completed');
        break;
      case 'overdue':
        filtered = filtered.filter(a => a.is_overdue && a.completion_status !== 'completed');
        break;
      default:
        break;
    }

    setFilteredAssignments(filtered);
  };

  const handleStatusUpdate = async (assignmentId: number, newStatus: string) => {
    try {
      setUpdatingStatus(assignmentId);
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API_BASE_URL}/api/student/assignments/${assignmentId}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Update local state
      setAssignments(assignments.map(a => 
        a.id === assignmentId ? { ...a, completion_status: newStatus } : a
      ));
    } catch (err: any) {
      setToast({
        open: true,
        severity: 'error',
        message: err.response?.data?.error || t('studentAssignments.updateStatusError')
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDownload = async (assignmentId: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(
        `${API_BASE_URL}/api/student/assignments/${assignmentId}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.download_url) {
        // Open the signed URL (same approach as tailored preview)
        const popup = window.open(response.data.download_url, '_blank', 'noopener,noreferrer');
        // Popup blockers can prevent window.open; fallback to same-tab navigation
        if (!popup) {
          window.location.href = response.data.download_url;
        }
      } else {
        setToast({
          open: true,
          severity: 'error',
          message: t('studentAssignments.downloadError')
        });
      }
    } catch (err: any) {
      setToast({
        open: true,
        severity: 'error',
        message: err.response?.data?.error || t('studentAssignments.downloadError')
      });
    }
  };
  
  const handleOpenSubmitDialog = async (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setSubmissionText('');
    setSubmissionNotes('');
    setUploadedFile(null);
    setSubmitError('');
    setCurrentSubmission(null);
    setSubmitDialogOpen(true);
    
    // Fetch existing submission if any
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(
        `${API_BASE_URL}/api/student/assignments/${assignment.id}/submission`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data.submission) {
        setCurrentSubmission(response.data.submission);
        setSubmissionText(response.data.submission.submission_text || '');
        setSubmissionNotes(response.data.submission.submission_notes || '');
      }
    } catch (err) {
      console.error('Error fetching submission:', err);
    }
  };
  
  const handleCloseSubmitDialog = () => {
    setSubmitDialogOpen(false);
    setSelectedAssignment(null);
    setCurrentSubmission(null);
  };
  
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSubmitError(t('studentAssignments.submission.fileTooLarge'));
        return;
      }
      setUploadedFile(file);
      setSubmitError('');
    }
  };
  
  const handleUploadFile = async () => {
    if (!selectedAssignment || !uploadedFile) return;
    
    setSubmitting(true);
    setSubmitError('');
    setUploadProgress(0);
    
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', uploadedFile);
      
      await axios.post(
        `${API_BASE_URL}/api/student/assignments/${selectedAssignment.id}/submission/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setUploadProgress(progress);
          }
        }
      );
      
      // Refresh submission data
      const response = await axios.get(
        `${API_BASE_URL}/api/student/assignments/${selectedAssignment.id}/submission`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data.submission) {
        setCurrentSubmission(response.data.submission);
      }
      
      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || t('studentAssignments.submission.uploadFailed'));
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };
  
  const handleSaveDraft = async () => {
    if (!selectedAssignment) return;
    
    setSubmitting(true);
    setSubmitError('');
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/student/assignments/${selectedAssignment.id}/submission`,
        {
          action: 'save',
          submission_text: submissionText,
          submission_notes: submissionNotes
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setCurrentSubmission(response.data.submission);
      
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || t('studentAssignments.submission.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSubmitWork = async () => {
    if (!selectedAssignment) return;
    
    setSubmitting(true);
    setSubmitError('');
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/student/assignments/${selectedAssignment.id}/submission`,
        {
          action: 'submit',
          submission_text: submissionText,
          submission_notes: submissionNotes
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setCurrentSubmission(response.data.submission);
      
      // Update assignments list
      setAssignments(assignments.map(a => 
        a.id === selectedAssignment.id 
          ? { ...a, completion_status: 'completed' }
          : a
      ));
      
      // Close dialog after short delay
      setTimeout(() => {
        handleCloseSubmitDialog();
        fetchAssignments();
      }, 1500);
      
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || t('studentAssignments.submission.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'submitted':
        return 'warning';
      case 'returned':
        return 'error';
      case 'reviewed':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div className="student-assignments-loading">
        <CircularProgress />
        <p>{t('studentAssignments.description')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-assignments-container">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="student-assignments-container">
      <div className="student-assignments-header">
        <div className="header-title">
          <AssignmentIcon sx={{ fontSize: 40, marginRight: 2 }} />
          <div>
            <h1>{t('studentAssignments.title')}</h1>
            <p>{t('studentAssignments.description')}</p>
          </div>
        </div>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t('studentAssignments.filters.label', 'Filter')}</InputLabel>
            <Select
              value={filter}
              label={t('studentAssignments.filters.label', 'Filter')}
              onChange={(e) => setFilter(e.target.value)}
              aria-label={t('studentAssignments.filters.label', 'Filter')}
            >
              <MenuItem value="all">{t('studentAssignments.filters.all')}</MenuItem>
              <MenuItem value="pending">{t('studentAssignments.filters.pending')}</MenuItem>
              <MenuItem value="inProgress">{t('studentAssignments.filters.inProgress')}</MenuItem>
              <MenuItem value="overdue">{t('studentAssignments.filters.overdue')}</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                size="small"
                aria-label={t('studentAssignments.showCompleted', 'Show completed')}
              />
            }
            label={t('studentAssignments.showCompleted', 'Show completed')}
          />
        </Box>
      </div>

      {filteredAssignments.length === 0 ? (
        <div className="no-assignments">
          <AssignmentIcon sx={{ fontSize: 80, color: '#ccc' }} />
          <Typography variant="h6" color="text.secondary">
            {assignments.length === 0 ? t('studentAssignments.noAssignments') : 'No assignments match the filter'}
          </Typography>
        </div>
      ) : (
        <div className="assignments-grid">
          {filteredAssignments.map((assignment) => (
            <Card key={assignment.id} className={`assignment-card ${assignment.is_overdue && assignment.completion_status !== 'completed' ? 'overdue' : ''}`}>
              <CardContent>
                <div className="assignment-header">
                  <Typography variant="h6" component="h2">
                    {assignment.document_name}
                  </Typography>
                  {assignment.is_overdue && assignment.completion_status !== 'completed' && (
                    <Chip label={t('studentAssignments.overdue')} color="error" size="small" />
                  )}
                </div>

                <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                  {assignment.has_tailored_version && (
                    <Chip 
                      label={t('studentAssignments.tailored')} 
                      color="secondary" 
                      size="small" 
                      icon={<CheckCircleIcon />}
                    />
                  )}
                </Box>

                <div className="assignment-details">
                  {assignment.teacher_name && (
                    <div className="detail-item">
                      <PersonIcon fontSize="small" />
                      <span><strong>{t('studentAssignments.teacher')}</strong> {assignment.teacher_name}</span>
                    </div>
                  )}

                  {assignment.subject && (
                    <div className="detail-item">
                      <span><strong>{t('studentAssignments.subject')}</strong> {assignment.subject}</span>
                    </div>
                  )}

                  {assignment.assigned_date && (
                    <div className="detail-item">
                      <span><strong>{t('studentAssignments.assignedDate')}</strong> {formatDate(assignment.assigned_date)}</span>
                    </div>
                  )}

                  {assignment.due_date && (
                    <div className="detail-item">
                      <span><strong>{t('studentAssignments.dueDate')}</strong> {formatDate(assignment.due_date)}</span>
                    </div>
                  )}

                  <div className="detail-item">
                    <span><strong>{t('studentAssignments.status')}</strong></span>
                    <Chip 
                      label={t(`studentAssignments.statuses.${assignment.completion_status}`)} 
                      color={getStatusColor(assignment.completion_status) as any}
                      size="small"
                    />
                  </div>
                </div>

                <div className="assignment-actions">
                  {assignment.has_interactive_version && (
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/student/assignments/${assignment.id}/interactive`)}
                      fullWidth
                      sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, mb: 1 }}
                      aria-label={t('studentAssignments.openInteractive')}
                    >
                      {t('studentAssignments.openInteractive')}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(assignment.id)}
                    fullWidth
                  >
                    {t('studentAssignments.download')}
                  </Button>

                  {/* Quiz buttons -- one per assigned quiz */}
                  {assignment.quizzes.map((quiz) => {
                    const status = quiz.latest_status;
                    const isReviewed = status === 'reviewed';
                    const isPending = status === 'graded';
                    const isReturned = status === 'returned';
                    const isInProgress = status === 'in_progress';

                    let label = quiz.quiz_title || t('quiz.taking.takeQuiz');
                    let variant: 'contained' | 'outlined' = 'contained';
                    let sx: any = { bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } };
                    let disabled = false;

                    if (isReviewed) {
                      label = `${quiz.quiz_title || t('quiz.taking.quizScore')}: ${quiz.best_percentage}%`;
                      variant = 'outlined';
                      sx = { borderColor: '#4caf50', color: '#4caf50' };
                    } else if (isPending) {
                      label = `${quiz.quiz_title || ''} — ${t('studentAssignments.statuses.submitted', 'Submitted')}`;
                      variant = 'outlined';
                      sx = { borderColor: '#ed6c02', color: '#ed6c02' };
                      disabled = true;
                    } else if (isReturned) {
                      label = `${quiz.quiz_title || ''} — ${t('studentAssignments.returnedTryAgain', 'Returned - Try Again')}`;
                      variant = 'contained';
                      sx = { bgcolor: '#ed6c02', '&:hover': { bgcolor: '#e65100' } };
                    } else if (isInProgress) {
                      label = `${quiz.quiz_title || ''} — ${t('studentAssignments.continueQuiz', 'Continue Quiz')}`;
                    }

                    return (
                      <Button
                        key={quiz.quiz_assignment_id}
                        variant={variant}
                        startIcon={<QuizIcon />}
                        onClick={() =>
                          navigate(
                            `/student/quiz/${quiz.quiz_assignment_id}?assignmentId=${assignment.id}`
                          )
                        }
                        fullWidth
                        disabled={disabled}
                        sx={sx}
                        aria-label={label}
                      >
                        {label}
                      </Button>
                    );
                  })}

                  {/* Submit Work Button - only for submittable assignments */}
                  {assignment.assignment_type !== 'informational' && assignment.completion_status !== 'reviewed' && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SendIcon />}
                      onClick={() => handleOpenSubmitDialog(assignment)}
                      fullWidth
                    >
                      {assignment.completion_status === 'completed' 
                        ? t('studentAssignments.viewSubmission')
                        : t('studentAssignments.submitWork')
                      }
                    </Button>
                  )}

                  {assignment.assignment_type !== 'informational' && assignment.completion_status === 'assigned' && (
                    <Button
                      variant="outlined"
                      onClick={() => handleStatusUpdate(assignment.id, 'in_progress')}
                      disabled={updatingStatus === assignment.id}
                      fullWidth
                    >
                      {updatingStatus === assignment.id ? (
                        <CircularProgress size={24} />
                      ) : (
                        t('studentAssignments.markInProgress')
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Submit Work Dialog */}
      <Dialog
        open={submitDialogOpen}
        onClose={handleCloseSubmitDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedAssignment?.document_name}
          {currentSubmission?.status === 'graded' && (
            <Chip 
              label={t('studentAssignments.statuses.graded')} 
              color="success" 
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
          {currentSubmission?.status === 'submitted' && (
            <Chip 
              label={t('studentAssignments.statuses.submitted')} 
              color="primary" 
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          
          {/* Show grade and feedback if graded */}
          {currentSubmission?.status === 'graded' && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <GradeIcon color="success" />
                <Typography variant="h6">
                  {t('studentAssignments.submission.grade')}: {currentSubmission.grade || currentSubmission.points_earned}
                  {currentSubmission.points_possible && ` / ${currentSubmission.points_possible}`}
                </Typography>
              </Box>
              {currentSubmission.teacher_feedback && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('studentAssignments.submission.teacherFeedback')}:
                  </Typography>
                  <Typography>{currentSubmission.teacher_feedback}</Typography>
                </Box>
              )}
            </Box>
          )}
          
          {/* Show return feedback if returned */}
          {currentSubmission?.status === 'returned' && currentSubmission.teacher_feedback && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                {t('studentAssignments.submission.returnedForRevision')}:
              </Typography>
              {currentSubmission.teacher_feedback}
            </Alert>
          )}
          
          {/* Text submission area */}
          <TextField
            label={t('studentAssignments.submission.yourWork')}
            multiline
            rows={6}
            fullWidth
            variant="outlined"
            value={submissionText}
            onChange={(e) => setSubmissionText(e.target.value)}
            disabled={submitting || currentSubmission?.status === 'graded'}
            placeholder={t('studentAssignments.submission.workPlaceholder')}
            sx={{ mb: 2 }}
            aria-label={t('studentAssignments.submission.yourWork')}
          />
          
          {/* Notes field */}
          <TextField
            label={t('studentAssignments.submission.notes')}
            multiline
            rows={2}
            fullWidth
            variant="outlined"
            value={submissionNotes}
            onChange={(e) => setSubmissionNotes(e.target.value)}
            disabled={submitting || currentSubmission?.status === 'graded'}
            placeholder={t('studentAssignments.submission.notesPlaceholder')}
            sx={{ mb: 2 }}
            aria-label={t('studentAssignments.submission.notes')}
          />
          
          {/* File upload section */}
          {currentSubmission?.status !== 'graded' && (
            <Box sx={{ mb: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('studentAssignments.submission.uploadFile')}
              </Typography>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                aria-label={t('studentAssignments.submission.uploadFile')}
              />
              
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
              >
                {t('studentAssignments.submission.chooseFile')}
              </Button>
              
              {uploadedFile && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{uploadedFile.name}</Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleUploadFile}
                    disabled={submitting}
                  >
                    {t('studentAssignments.submission.upload')}
                  </Button>
                </Box>
              )}
              
              {uploadProgress > 0 && uploadProgress < 100 && (
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
              )}
              
              {currentSubmission?.has_file && currentSubmission.original_filename && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="success.main">
                    {t('studentAssignments.submission.uploadedFile')}: {currentSubmission.original_filename}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          {/* Submission info */}
          {currentSubmission?.submitted_at && (
            <Typography variant="caption" color="text.secondary">
              {t('studentAssignments.submission.submittedOn')}: {new Date(currentSubmission.submitted_at).toLocaleString()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmitDialog}>
            {t('common.close')}
          </Button>
          
          {currentSubmission?.status !== 'graded' && currentSubmission?.status !== 'submitted' && (
            <>
              <Button 
                onClick={handleSaveDraft} 
                disabled={submitting}
                startIcon={<EditIcon />}
              >
                {submitting ? <CircularProgress size={20} /> : t('studentAssignments.submission.saveDraft')}
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSubmitWork}
                disabled={submitting}
                startIcon={<SendIcon />}
              >
                {submitting ? <CircularProgress size={20} /> : t('studentAssignments.submission.submit')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StudentAssignments;

